import { DateTime } from 'luxon';
import { action, computed, makeObservable, observable } from 'mobx';

import type { WeeklyWorkLog } from '@/types';
import { parseDuration } from '@/utils/parseDuration';

export interface PendingWorklog {
  duration: number;
  taskKey: string;
  start: string;
}

export class WorklogsModel {
  private _weeklyWorklogs: Map<string, WeeklyWorkLog> = new Map();
  private _pendingWorklogs: Map<string, PendingWorklog> = new Map();

  constructor() {
    makeObservable<WorklogsModel, '_weeklyWorklogs' | '_pendingWorklogs'>(this, {
      _weeklyWorklogs: observable,
      _pendingWorklogs: observable,
      dailyWorkMinutes: computed,
      setWorklogs: action.bound,
      addPending: action.bound,
      removePending: action.bound,
      clear: action.bound,
    });
  }

  get dailyWorkMinutes(): Record<string, number> {
    const result: Record<string, number> = {};

    for (const wl of this._weeklyWorklogs.values()) {
      const date = DateTime.fromISO(wl.start).toISODate();

      if (date) {
        result[date] = (result[date] || 0) + parseDuration(wl.duration);
      }
    }

    for (const pending of this._pendingWorklogs.values()) {
      const date = DateTime.fromISO(pending.start).toISODate();

      if (date) {
        result[date] = (result[date] || 0) + pending.duration;
      }
    }

    return result;
  }

  get weeklyWorklogs(): Map<string, WeeklyWorkLog> {
    return this._weeklyWorklogs;
  }

  get pendingWorklogs(): Map<string, PendingWorklog> {
    return this._pendingWorklogs;
  }

  getWorklog(id: string): WeeklyWorkLog | undefined {
    return this._weeklyWorklogs.get(id);
  }

  getPending(id: string): PendingWorklog | undefined {
    return this._pendingWorklogs.get(id);
  }

  setWorklogs(worklogs: WeeklyWorkLog[]): void {
    this._weeklyWorklogs.clear();

    for (const wl of worklogs) {
      this._weeklyWorklogs.set(String(wl.id), wl);
      this._pendingWorklogs.delete(String(wl.id));
    }
  }

  addPending(worklogId: string, data: PendingWorklog): void {
    this._pendingWorklogs.set(worklogId, data);
  }

  removePending(worklogId: string): void {
    this._pendingWorklogs.delete(worklogId);
  }

  clear(): void {
    this._weeklyWorklogs.clear();
    this._pendingWorklogs.clear();
  }
}
